import { ChatType } from "@/server/types/chat";
import { useTypingLabel } from "./useTypingLabel";
import TypingDots from "./TypingDots";

interface TypingRowProps {
  chatId: string | null | undefined;
  chatType: ChatType;
}

/**
 * The typing row rendered near the bottom of the message list. Renders nothing
 * when no one (other than self) is typing.
 *
 * Placement note: MessageList is `flex-col-reverse`, so the visual bottom is the
 * START of the reversed flow. This row is mounted immediately after the bottom
 * anchor and BEFORE the message map in DOM order, which keeps it pinned to the
 * bottom and pushes content up only when the user is already at the bottom.
 */
export default function TypingRow({
  chatId,
  chatType,
}: TypingRowProps): JSX.Element | null {
  const label = useTypingLabel(chatId, chatType);

  if (!label) {
    return null;
  }

  return (
    // Outer wrapper matches the per-message row centering
    // (w-full lg:mx-auto lg:max-w-[1000px]) and the inner px-2 lg:px-3 matches
    // MessageListItem's horizontal padding, so the typing row's left edge lines
    // up with the conversation column instead of the raw viewport edge.
    <div className="w-full lg:mx-auto lg:max-w-[1000px]">
      <div
        data-testid="typing-row"
        aria-live="polite"
        className="flex min-w-0 items-center gap-2 px-2 py-1 text-sm text-muted-foreground lg:px-3"
      >
        <span className="oa-typing-label min-w-0 truncate">{label}</span>
        <TypingDots />
      </div>
    </div>
  );
}
